import styled, { DefaultTheme } from 'styled-components';
import { Card, Typography, Skeleton, Select } from 'antd';

const {Text} = Typography;

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 24px;
  min-height: 100vh;
  background: ${(props) => getThemeValue(props, '#171923', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

export const HeaderAvatar = styled.div<{ $color: string; $bg: string; $bgDark: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${(props) => getThemeValue(props, props.$bgDark, props.$bg)};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
`;

export const TitleSection = styled.div`
  min-width: 0;
`;

export const MainTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  line-height: 1.25;
  overflow-wrap: break-word;

  @media (min-width: 641px) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 420px;
  }

  @media (max-width: 480px) {
    font-size: 19px;
  }
`;

export const Subtitle = styled(Text)`
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  line-height: normal;
`;

export const SubtitleMeta = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

// Fixed-width wrapper around the shared StoreSwitcher (commonComponents),
// so a long store name never pushes the trigger wider — it ellipsizes
// inside this box instead. Also overrides StoreSwitcher's own `flex: 1`,
// which would otherwise stretch it across the whole HeaderRow.
export const StoreSwitcherWrap = styled.div`
  width: 280px;
  max-width: 100%;
  flex-shrink: 0;

  & > div {
    width: 100%;
  }
`;

export const StatCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.2s ease;
  cursor: pointer;
  height: 100%;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => getThemeValue(props, '0 8px 24px rgba(0, 0, 0, 0.35)', '0 8px 24px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 480px) {
      padding: 16px;
      gap: 12px;
    }
  }
`;

export const StatCardIcon = styled.div<{ bg?: string; color?: string }>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ bg }) => bg || 'rgba(24, 144, 255, 0.1)'};
  color: ${({ color }) => color || '#1890ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 17px;
  }
`;

export const StatCardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StatCardValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  line-height: 1.2;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

export const StatCardLabel = styled(Text)`
  display: block;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  margin-top: 2px;
`;

export const TrendBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ color }) => color};
  line-height: 1.4;

  svg {
    width: 11px;
    height: 11px;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
  width: 100%;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 576px) and (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 24px;
  }
`;


export const ValueSkeleton = styled(Skeleton.Input)`
  width: 100px;
  height: 32px;
`;


// Header Button Styles
export const HeaderButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.7;
  }
`;

export const HeaderIconWrapper = styled.span`
  font-size: 20px;
  color: #8c8c8c;
`;

// Charts row layout
export const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(360px, 100%), 1fr));
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

export const ChartCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  height: 100%;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 480px) {
      padding: 18px;
    }
  }
`;

export const ChartCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const ChartCardTitle = styled.h3`
  margin: 0 0 2px 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const ChartCardSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: #8c8c8c;
`;

export const StyledSelect = styled(Select)`
  &.ant-select {
    width: auto;
    min-width: 120px;
    height: 36px !important;
    border-radius: 12px !important;
    border: 1px solid ${(props) => getThemeValue(props, '#2d3748', '#e2e8f0')} !important;
    background: ${(props) => getThemeValue(props, '#161b26', '#f8fafc')} !important;
    box-shadow: none !important;
    padding: 0 12px !important;
    transition: border-color 0.2s ease;
  }

  &.ant-select:hover {
    border-color: #1890ff !important;
  }

  &.ant-select-focused {
    border-color: #1890ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.15) !important;
  }

  .ant-select-content-value {
    display: flex !important;
    align-items: center;
    height: 100%;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => getThemeValue(props, '#e2e8f0', '#334155')};
  }

  .ant-select-suffix {
    color: ${(props) => getThemeValue(props, '#8695ab', '#94a3b8')};
    font-size: 11px;
  }
`;

export const ChartCanvasContainer = styled.div<{ height?: number }>`
  height: ${({ height }) => height || 220}px;
  width: 100%;
`;

export const ChartCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#2d3748', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#161b26', '#f8fafc')};
  color: ${(props) => getThemeValue(props, '#8695ab', '#64748b')};
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

export const ModalChartContainer = styled.div<{ height?: number }>`
  height: ${({ height }) => height || 460}px;
  width: 100%;
  padding-top: 8px;
`;

export const ModalSubtitle = styled.p`
  margin: -8px 0 20px 0;
  font-size: 13px;
  color: #8c8c8c;
`;

// "New Orders" banner (Orders Summary card)
export const NewOrdersBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${(props) => getThemeValue(props, 'rgba(24, 144, 255, 0.12)', 'rgba(24, 144, 255, 0.08)')};
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const NewOrdersLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const NewOrdersBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 8px;
  background: #1890ff;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

export const NewOrdersLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #52c41a;
  display: inline-block;
`;

export const NewOrdersLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #1890ff;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

// Donut chart + breakdown legend
export const DonutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const DonutChartWrapper = styled.div`
  position: relative;
  width: 130px;
  height: 130px;
  flex-shrink: 0;
  margin: 0 auto;
`;

export const DonutCenterValue = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const BreakdownList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 180px;
  width: 100%;
`;

export const BreakdownRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const BreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
`;

export const BreakdownLabel = styled.span<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => getThemeValue(props, '#e2e8f0', '#4a5568')};
  font-weight: 500;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color || '#1890ff'};
    flex-shrink: 0;
  }
`;

export const BreakdownValue = styled.span`
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const BreakdownBarTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 4px;
  background: ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
  overflow: hidden;
`;

export const BreakdownBarFill = styled.div<{ percent: number; color?: string }>`
  height: 100%;
  border-radius: 4px;
  width: ${({ percent }) => Math.min(Math.max(percent, 0), 100)}%;
  background: ${({ color }) => color || '#1890ff'};
`;

// Revenue card legend
export const RevenueLegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 20px;
`;

export const RevenueLegendItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

export const RevenueLegendLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RevenueSwatch = styled.span<{ color?: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color || '#1890ff'};
  flex-shrink: 0;
`;

export const RevenueLegendLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1;
`;

export const RevenueLegendValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

// Orders Summary mini-stat tiles (Pending / Ready / Completed)
export const MiniStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

export const MiniStatTile = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
`;

export const MiniStatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  line-height: 1.2;
`;

export const MiniStatLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
`;

/* Platform filter — sits above the stat cards, filters the whole dashboard
   by sales channel (All / Web / Mobile / Kiosk). */
export const PlatformFilterRow = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

// Used both standalone (above the stat cards) and inline in each chart
// card's header (next to the period StyledSelect) — sized to match that
// select (36px tall, 12px radius) so it drops in consistently either way.
export const PlatformFilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#2d3748', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#161b26', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 1px 4px rgba(0, 0, 0, 0.3)', '0 1px 4px rgba(0, 0, 0, 0.06)')};
  color: ${(props) => getThemeValue(props, '#e2e8f0', '#1a202c')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: #1890ff;
  }
`;

export const PlatformFilterChevron = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  font-size: 11px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')};
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

export const PlatformDropdownContainer = styled.div`
  width: 220px;
  padding: 8px;
  border-radius: 14px;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 12px 24px rgba(0, 0, 0, 0.45)', '0 12px 24px rgba(15, 23, 42, 0.12)')};
`;

export const PlatformOptionItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  background: ${({ $selected }) =>
    $selected ? 'rgba(24, 144, 255, 0.12)' : 'transparent'};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(24, 144, 255, 0.18)' : 'rgba(148, 163, 184, 0.12)'};
  }

  & + & {
    margin-top: 2px;
  }
`;

export const PlatformOptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  font-size: 14px;
  flex-shrink: 0;
`;

export const PlatformOptionLabel = styled.span`
  flex: 1;
  font-size: 13.5px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#e2e8f0', '#1a202c')};
`;

export const PlatformCheckIcon = styled.span`
  display: flex;
  align-items: center;
  color: #1890ff;
  font-size: 12px;
  flex-shrink: 0;
`;